// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import config from "@config";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  SectionComponent,
  SchemaForm,
} from "@components";
import actionCreators from "@modules/reporting/actionCreator";
import {
  showRTWReferralDetailsModal,
  selectedRTWReferralDetails,
  selectReferralCommonData,
} from "@modules/reporting/selectors/referral-tracker";
import type {
  CommonData,
  ReferralRTWDetails,
} from "@modules/reporting/reducer/referral-tracker/types";

type OwnProps = $Exact<{
  t: Object,
}>;

type PropsFromState = $Exact<{
  showModal: boolean,
  details: ReferralRTWDetails,
  commonData: CommonData,
}>;

type PropsFromDispatch = $Exact<{
  toggleRTWReferralDetailsModal: typeof actionCreators.ToggleRTWReferralDetailsModal,
  submitRTWReferralData: typeof actionCreators.SubmitRTWReferralData,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class ReferralRTWModal extends Component<Props> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }

  handleCancel = () => {
    this.props.toggleRTWReferralDetailsModal();
  };
  submitForm = () => {
    if (this.formRef) {
      this.formRef.current.submit();
    }
  };
  handleSubmit = (formData: Object) => {
    this.props.submitRTWReferralData({
      clientId: this.props.commonData.clientId,
      supplierReferralId: this.props.commonData.referralId,
      requestBody: { fitDate: formData.fitDate, rtwDate: formData.rtwDate },
    });
  };
  get schema() {
    return {
      required: ["fitDate", "rtwDate"],
      type: "object",
      properties: {
        startDate: {
          title: this.props.t("START_DATE"),
          type: "string",
        },
        fitDate: {
          title: this.props.t("FIT_DATE_AND_TIME"),
          type: "string",
          format: "date-time",
        },
        rtwDate: {
          title: this.props.t("RTW_DATE_AND_TIME"),
          type: "string",
          format: "date-time",
        },
      },
    };
  }
  get uiSchema() {
    return {
      startDate: {
        "ui:widget": "date-picker",
        "ui:options": {
          dateFormat: config.dateTimeFormat,
          disabled: true,
          enableTime: true,
        },
      },
      rtwDate: {
        "ui:widget": "date-picker",
        "ui:options": {
          enableTime: true,
          dateFormat: config.dateTimeFormat,
        },
      },
      fitDate: {
        "ui:widget": "date-picker",
        "ui:options": {
          enableTime: true,
          dateFormat: config.dateTimeFormat,
        },
      },
    };
  }

  render() {
    if (!this.props.showModal) {
      return null;
    }
    return (
      <Modal className="modal-normal">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("RTW_REFERRAL_DETAILS")} #{this.props.commonData.referralId}
        </ModalHeader>
        <ModalBody>
          <SchemaForm
            ref={this.formRef}
            formData={{
              startDate: this.props.commonData.startDate,
              rtwDate: this.props.details.rtwDate,
              fitDate: this.props.details.fitDate,
            }}
            schema={this.schema}
            onSubmit={this.handleSubmit}
            uiSchema={this.uiSchema}
            externalSubmit
          />
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CLOSE")}
          </Button>
          <Button materialIcon="replay_circle_filled" onClick={this.submitForm}>
            {this.props.t("SUBMIT")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    showModal: showRTWReferralDetailsModal(state),
    details: selectedRTWReferralDetails(state),
    commonData: selectReferralCommonData(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  toggleRTWReferralDetailsModal: actionCreators.ToggleRTWReferralDetailsModal,
  submitRTWReferralData: actionCreators.SubmitRTWReferralData,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(ReferralRTWModal);
